import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, PlusIcon, RefreshCw } from "lucide-react";
import { Button } from "@workspace/web-ui/components/button";
import { Skeleton } from "@workspace/web-ui/components/skeleton";
import { getTasksByEncounterIdQuery } from "../../api/tasks/query";
import useAddNoteStore from "../../pages/patient-logs/store/add-note-store";
import useEncounterStore from "../../pages/patient-logs/store/encounter-store";
import { ViewNotesModal } from "../../pages/patient-logs/components/modals/view-notes-modal";
import { useGenerateNotesMutation, useUpdateSessionICD10Mutation } from "../../pages/patient-logs/api/mutations";
import { useState } from "react";
import { toast } from "sonner";
import { tasksKeys } from "../../api/tasks/keys";
import { ICD10Modal } from "../../pages/patient-logs/components/modals/icd10-modal";
import { useProgram } from "~/hooks/use-program";

interface NotesContainerProps {
    encounterId: string;
    patientId: string;
    notes?: {
        assessment: string;
        created_at: string;
        objective: string;
        plan: string;
        subjective: string;
        updated_at: string;
    } | null;
    icd?: string;
}

const NotesContainer = ({ encounterId, patientId, notes: notesProp, icd }: NotesContainerProps) => {
    const { data, isLoading } = useQuery(getTasksByEncounterIdQuery(encounterId));
    const queryClient = useQueryClient();
    const { program } = useProgram();
    const [isICD10ModalOpen, setIsICD10ModalOpen] = useState(false);
    const [isGenerateFlow, setIsGenerateFlow] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);
    const { isNoteModalOpen, selectedNoteData, closeNoteModal, openNoteModal } = useAddNoteStore();
    const { setEncounterId } = useEncounterStore();

    const generateNotesMutation = useGenerateNotesMutation();
    const updateSessionICD10Mutation = useUpdateSessionICD10Mutation();

    // Use notes from query first, fallback to prop if not available
    const notes = data?.notes ?? notesProp;

    const handleAddNote = () => {
        setEncounterId(encounterId);
        openNoteModal(notes ?? undefined, patientId);
    }

    const handleGenerateNotes = (icd10Code?: string, isRegenerate: boolean = false) => {
        // If called from regenerate button, generate directly with the ICD10 code (or without if not available)
        if (isRegenerate) {
            generateNotesMutation.mutate(
                { encounterId, patientId, icd10_code: icd10Code || undefined, program },
                {
                    onSuccess: async () => {
                        toast.success("Notes regenerated successfully!");
                        setIsRefetching(true);
                        try {
                            await queryClient.refetchQueries({
                                queryKey: tasksKeys.getTasksByEncounterId(encounterId),
                            });
                            // Close the modal after successful regeneration
                            closeNoteModal();
                        } catch (error) {
                            setTimeout(() => {
                                setIsRefetching(false);
                            }, 500);
                        } finally {
                            setIsRefetching(false);
                        }
                    },
                    onError: () => {
                        toast.error("Failed to regenerate notes");
                    },
                }
            );
        } else {
            // Otherwise, open ICD10 modal for new generation
            setIsGenerateFlow(true);
            setIsICD10ModalOpen(true);
        }
    };

    // const handleOpenUpdateModal = () => {
    //     setIsGenerateFlow(false);
    //     setIsICD10ModalOpen(true);
    // };

    const handleICD10Submit = async (data: { icd10_code?: string }) => {
        if (isGenerateFlow) {
            // Generate notes with ICD10 code
            generateNotesMutation.mutate(
                { encounterId, patientId, icd10_code: data.icd10_code, program },
                {
                    onSuccess: async () => {
                        toast.success("Notes generated successfully!");
                        setIsRefetching(true);
                        // Wait for the query to refetch before closing the modal
                        try {
                            await queryClient.refetchQueries({
                                queryKey: tasksKeys.getTasksByEncounterId(encounterId),
                            });
                            setIsICD10ModalOpen(false);
                            setIsGenerateFlow(false);
                        } catch (error) {
                            // Even if refetch fails, close the modal after a short delay
                            setTimeout(() => {
                                setIsICD10ModalOpen(false);
                                setIsGenerateFlow(false);
                            }, 500);
                        } finally {
                            setIsRefetching(false);
                        }
                    },
                    onError: () => {
                        toast.error("Failed to generate notes");
                    },
                }
            );
        } else {
            // Update session with ICD10 code
            updateSessionICD10Mutation.mutate(
                { encounterId, icd10_code: data.icd10_code, patientId, program },
                {
                    onSuccess: async () => {
                        toast.success("Session updated successfully!");
                        setIsRefetching(true);
                        // Wait for the query to refetch before closing the modal
                        try {
                            await queryClient.refetchQueries({
                                queryKey: tasksKeys.getTasksByEncounterId(encounterId),
                            });
                            setIsICD10ModalOpen(false);
                        } catch (error) {
                            // Even if refetch fails, close the modal after a short delay
                            setTimeout(() => {
                                setIsICD10ModalOpen(false);
                            }, 500);
                        } finally {
                            setIsRefetching(false);
                        }
                    },
                    onError: () => {
                        toast.error("Failed to update session");
                    },
                }
            );
        }
    };

    if (isLoading) {
        return <Skeleton className="w-full h-32" />;
    }

    return (
        <>
            {notes ? (
                <div className="flex flex-col gap-3 w-full p-4 rounded-lg bg-[#F1F4F9]">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#4272DD]" />
                            <h2 className="font-medium text-xl">Notes</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                className="button-primary !bg-[#4272DD] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                onClick={handleAddNote}
                            >
                                <PlusIcon className="w-4 h-4 text-white" />
                                Edit Note
                            </Button>
                            {/* {program === "urgent-care" && (
                            <Button
                                className="button-primary !bg-[#4272DD] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                onClick={handleOpenUpdateModal}
                                disabled={updateSessionICD10Mutation.isPending}
                            >
                                <Upload className="w-4 h-4 text-white" />
                                {updateSessionICD10Mutation.isPending ? 'Update...' : 'Update Icd10'}
                            </Button>
                            )} */}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className="bg-white rounded-lg p-3">
                            <p className="text-xs font-medium text-[#6B7280] mb-1">Subjective</p>
                            <p className="text-sm text-[#1A202C]">{notes.subjective}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                            <p className="text-xs font-medium text-[#6B7280] mb-1">Objective</p>
                            <p className="text-sm text-[#1A202C]">{notes.objective || ""}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                            <p className="text-xs font-medium text-[#6B7280] mb-1">Assessment</p>
                            <p className="text-sm text-[#1A202C]">{notes.assessment}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                            <p className="text-xs font-medium text-[#6B7280] mb-1">Plan</p>
                            <div className="text-sm space-y-2">
                                {notes.plan?.split(/(?=\d+\.)/).filter(Boolean).map((item, index) => (
                                    <p key={index}>{item.trim()}</p>
                                )) || ""}
                            </div>
                        </div>
                    </div>
                    {icd && (
                        <div className="bg-white rounded-lg p-3">
                            <p className="text-xs font-medium text-[#6B7280] mb-1">ICD-10 Code</p>
                            <p className="text-sm text-[#1A202C]">{icd}</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex justify-between items-center w-full p-3 bg-[#F8F9FA] rounded-lg">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#4272DD]" />
                        <h2 className="font-medium text-xl">Notes</h2>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            className="button-primary !bg-white !text-[#4272DD] flex gap-2 items-center !shadow-none w-fit !border !border-[#4272DD]"
                            onClick={handleAddNote}
                            disabled={generateNotesMutation.isPending}
                        >
                            <PlusIcon className="w-4 h-4 text-[#4272DD]" />
                            Add Note
                        </Button>
                        <Button
                            className="button-primary !bg-[#4272DD] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                            onClick={() => handleGenerateNotes()}
                            disabled={generateNotesMutation.isPending}
                        >
                            <RefreshCw className={`w-4 h-4 text-white ${generateNotesMutation.isPending ? 'animate-spin' : ''}`} />
                            {generateNotesMutation.isPending ? 'Generating...' : 'Generate'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Notes Modal - Commented out as we now use generate endpoint */}
            <ViewNotesModal
                data={selectedNoteData}
                patientId={patientId}
                open={isNoteModalOpen}
                handleGenerateNotes={handleGenerateNotes}
                isPending={generateNotesMutation.isPending || isRefetching}
                onOpenChange={closeNoteModal}
                onSubmit={handleICD10Submit}
                isLoading={isGenerateFlow ? (generateNotesMutation.isPending || isRefetching) : (updateSessionICD10Mutation.isPending || isRefetching)}
                isGenerateFlow={isGenerateFlow}
                icd10Code={icd}
            />

            {/* ICD10 Modal - Used for both Generate and Update */}
            <ICD10Modal
                open={isICD10ModalOpen}
                onOpenChange={(open) => {
                    setIsICD10ModalOpen(open);
                    if (!open) {
                        setIsGenerateFlow(false);
                    }
                }}
                onSubmit={handleICD10Submit}
                isLoading={isGenerateFlow ? (generateNotesMutation.isPending || isRefetching) : (updateSessionICD10Mutation.isPending || isRefetching)}
                isGenerateFlow={isGenerateFlow}
            />
        </>
    );
};

export default NotesContainer;

